import { useState, useMemo, useCallback, useEffect } from 'react';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { GAME_CONSTANTS } from '../../config/gameConstants';
import { ComponentOrderModal } from './ComponentOrderModal';
import type { AutoOrderSetting } from '@smartphone-tycoon/shared-core';

// Direct Zustand store imports
import { useInventoryStore } from '../../stores/inventoryStore';
import { useCompanyStore } from '../../stores/companyStore';
import { inventoryManagementService } from '../../services/tycoon/inventoryManagementService';

// Optimized selectors
import { 
  useCompanyFunds,
  useGameDate
} from '../../stores/selectors/optimizedSelectors';

// Import component categories data
import { components as componentCategories } from '@smartphone-tycoon/shared-core';
import { componentReleaseService } from '../../services/tycoon/componentReleaseService';

import type { Product } from '@smartphone-tycoon/shared-core';

type SortField = 'name' | 'category' | 'manufacturer' | 'releaseDate' | 'cost' | 'techScore' | 'inventory' | 'pendingOrders';
type SortOrder = 'asc' | 'desc';

interface ComponentsListProps {
  gameDate?: {
    year: number;
    month: number;
    day: number;
  };
  products?: Product[];
  cash?: number;
  onCashUpdate?: (newCash: number) => void;
  onEvent?: (event: unknown) => void;
}

interface ComponentItem {
  value: string;
  name: string;
  cost: number;
  techScore: number;
  manufacturer: string;
  specs?: string;
  category: string;
  categoryId: string;
  releaseDate: { day: number; month: number; year: number };
}

function getAutoOrderTooltipText(setting: AutoOrderSetting | undefined): string {
  if (!setting) return '';
  const parts: string[] = [];
  if (setting.autoOrderThreshold !== null) {
    parts.push(`when below ${setting.autoOrderThreshold.toLocaleString()} units`);
  }
  if (setting.recurringOrder?.enabled) {
    parts.push(`${setting.recurringOrder.frequency}ly`);
  }
  return `Auto-order: ${parts.join(' + ')}`;
}

export function ComponentsList({ 
  gameDate: gameDateProp, 
  products: productsProp = [], 
  cash: cashProp = 0, 
  onCashUpdate, 
  onEvent 
}: ComponentsListProps) {
  // Direct store access
  const gameDateFromStore = useGameDate();
  const companyFunds = useCompanyFunds();
  
  // Use prop values if provided, otherwise use store
  const gameDate = gameDateProp || gameDateFromStore;
  const cash = cashProp || companyFunds;
  
  // Note: productsProp is kept for backward compatibility but not used
  // The component now gets all product data from the inventory store
  void productsProp; // Explicitly mark as intentionally unused
  
  // Inventory store selectors
  const components = useInventoryStore(state => state.components);
  const purchaseOrders = useInventoryStore(state => state.purchaseOrders);
  const createPurchaseOrder = useInventoryStore(state => state.createPurchaseOrder);
  const autoOrderSettings = useInventoryStore(state => state.autoOrderSettings);
  const setAutoOrderSetting = useInventoryStore(state => state.setAutoOrderSetting);
  const removeAutoOrderSetting = useInventoryStore(state => state.removeAutoOrderSetting);
  const setCash = useCompanyStore(state => state.setCash);

  // Sync component inventory from database on mount
  useEffect(() => {
    inventoryManagementService.syncComponentInventoryToStore();
  }, []);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('releaseDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<{
    id: string;
    name: string;
    cost: number;
    manufacturer: string;
    category: string;
  } | null>(null);

  const formatDate = (date: { day: number; month: number; year: number }) => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return `${months[date.month - 1]} ${date.day}, ${date.year}`;
  };

  // Get all available components from categories - must be defined before handleComponentOrder
  const allComponents = useMemo(() => {
    // Get dynamic components based on current game date
    const availableComponentsMap = componentReleaseService.getAllAvailableComponents(
      gameDate.year,
      gameDate.month
    );

    // Flatten all available components across categories
    const components: ComponentItem[] = [];
    componentCategories.forEach(category => {
      const availableOptions = availableComponentsMap.get(category.id) || [];
      availableOptions.forEach(option => {
        components.push({
          ...option,
          category: category.name,
          categoryId: category.id
        });
      });
    });

    return components;
  }, [gameDate.year, gameDate.month]);

  // Handle component ordering
  const handleComponentOrder = useCallback(async (componentId: string, quantity: number) => {
    // Find component details
    const component = allComponents.find(c => c.value === componentId);
    if (!component) return;
    
    const totalCost = component.cost * quantity;
    
    // Validate totalCost
    if (!totalCost || isNaN(totalCost) || !isFinite(totalCost)) {
      console.error('Invalid total cost calculation:', { 
        componentCost: component.cost, 
        quantity, 
        totalCost 
      });
      if (onEvent) {
        onEvent({
          type: 'error',
          message: 'Invalid cost calculation. Please try again.'
        });
      }
      return;
    }
    
    // Check if we have enough funds
    if (cash < totalCost) {
      if (onEvent) {
        onEvent({
          type: 'error',
          message: `Insufficient funds to order ${quantity} units of ${component.name}`
        });
      }
      return;
    }
    
    try {
      // Create order through inventory service (this handles database and inventory tracking)
      const serviceOrder = await inventoryManagementService.placeComponentOrder(
        componentId, 
        quantity, 
        gameDate
      );
      
      // Calculate delivery date with proper month/year overflow
      const deliveryDays = gameDate.day + GAME_CONSTANTS.inventory.components.leadTimeDays;
      let deliveryDay = deliveryDays;
      let deliveryMonth = gameDate.month;
      let deliveryYear = gameDate.year;
      
      // Handle month overflow
      while (deliveryDay > 30) {
        deliveryDay -= 30;
        deliveryMonth++;
        if (deliveryMonth > 12) {
          deliveryMonth = 1;
          deliveryYear++;
        }
      }
      
      // Also create order in Zustand store for UI updates
      const order = {
        id: serviceOrder.orderId,
        supplierId: component.manufacturer,
        supplierName: component.manufacturer,
        orderDate: gameDate,
        expectedDelivery: {
          day: deliveryDay,
          month: deliveryMonth,
          year: deliveryYear
        },
        status: 'pending' as const,
        items: [{
          componentId,
          componentName: component.name,
          quantity,
          unitPrice: component.cost,
          totalPrice: totalCost
        }],
        totalAmount: totalCost,
        paymentStatus: 'pending' as const
      };
      
      // Update stores
      createPurchaseOrder(order);
      // Deduct the cost from company cash
      setCash(cash - totalCost);
      
      // Update legacy callback if provided
      if (onCashUpdate) {
        onCashUpdate(cash - totalCost);
      }
      
      // Trigger event if provided
      if (onEvent) {
        onEvent({
          type: 'component',
          title: 'Component Order Placed',
          description: `Ordered ${quantity.toLocaleString()} units of ${component.name} for ${formatCurrency(totalCost || 0)}`,
          impact: 'neutral'
        });
      }
      
      // console.log('Order placed:', order);
    } catch (error) {
      console.error('Failed to place order:', error);
      if (onEvent) {
        onEvent({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to place order'
        });
      }
    }
  }, [cash, gameDate, createPurchaseOrder, setCash, onCashUpdate, onEvent, allComponents]);

  // Handler to open order modal
  const handleOpenOrderModal = useCallback((component: ComponentItem) => {
    setSelectedComponent({
      id: component.value,
      name: component.name,
      cost: component.cost,
      manufacturer: component.manufacturer,
      category: component.categoryId,
    });
    setIsOrderModalOpen(true);
  }, []);

  // Handler to close order modal
  const handleCloseOrderModal = useCallback(() => {
    setIsOrderModalOpen(false);
    setSelectedComponent(null);
  }, []);

  // Handler for order from modal
  const handleModalOrder = useCallback(
    async (
      componentId: string,
      quantity: number,
      autoOrderSetting: Omit<AutoOrderSetting, 'componentId'>
    ) => {
      // FIRST: Always save/remove auto-order settings (this should never fail)
      // This ensures user preferences are saved even if the order placement fails
      if (
        autoOrderSetting.autoOrderThreshold !== null ||
        autoOrderSetting.recurringOrder.enabled
      ) {
        setAutoOrderSetting(componentId, {
          componentId,
          ...autoOrderSetting,
        });
      } else {
        removeAutoOrderSetting(componentId);
      }

      // THEN: Place order if quantity > 0 (can fail without affecting settings)
      if (quantity > 0) {
        await handleComponentOrder(componentId, quantity);
      }
    },
    [handleComponentOrder, setAutoOrderSetting, removeAutoOrderSetting]
  );

  // Filter components based on current game date
  const availableComponents = useMemo(() => {
    return allComponents.filter(component => {
      const releaseDate = new Date(
        component.releaseDate.year, 
        component.releaseDate.month - 1, 
        component.releaseDate.day
      );
      const currentDate = new Date(gameDate.year, gameDate.month - 1, gameDate.day);
      return releaseDate <= currentDate;
    });
  }, [allComponents, gameDate]);

  // Filter components based on search and category
  const filteredComponents = useMemo(() => {
    return availableComponents.filter(component => {
      const matchesSearch = searchTerm === '' || 
        component.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (component.specs && component.specs.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || component.categoryId === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [availableComponents, searchTerm, selectedCategory]);

  // Helper to get pending orders for a component
  const getComponentPendingOrders = useCallback((componentId: string) => {
    return purchaseOrders
      .filter(order => order.status === 'pending')
      .reduce((sum, order) => {
        // Sum up quantities from all items in the order that match this component
        const componentQuantity = order.items
          .filter(item => item.componentId === componentId)
          .reduce((itemSum, item) => itemSum + item.quantity, 0);
        return sum + componentQuantity;
      }, 0);
  }, [purchaseOrders]);
  
  // Sort components
  const sortedComponents = useMemo(() => {
    const sorted = [...filteredComponents];
    
    sorted.sort((a, b) => {
      let compareValue = 0;
      
      switch (sortField) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'category':
          compareValue = a.category.localeCompare(b.category);
          break;
        case 'manufacturer':
          compareValue = a.manufacturer.localeCompare(b.manufacturer);
          break;
        case 'releaseDate': {
          const dateA = new Date(a.releaseDate.year, a.releaseDate.month - 1, a.releaseDate.day);
          const dateB = new Date(b.releaseDate.year, b.releaseDate.month - 1, b.releaseDate.day);
          compareValue = dateA.getTime() - dateB.getTime();
          break;
        }
        case 'cost':
          compareValue = a.cost - b.cost;
          break;
        case 'techScore':
          compareValue = a.techScore - b.techScore;
          break;
        case 'inventory': {
          const stockA = components.get(a.value)?.quantity || 0;
          const stockB = components.get(b.value)?.quantity || 0;
          compareValue = stockA - stockB;
          break;
        }
        case 'pendingOrders': {
          const pendingA = getComponentPendingOrders(a.value);
          const pendingB = getComponentPendingOrders(b.value);
          compareValue = pendingA - pendingB;
          break;
        }
      }
      
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
    
    return sorted;
  }, [filteredComponents, sortField, sortOrder, components, getComponentPendingOrders]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 text-theme-text-muted" />;
    }
    return sortOrder === 'asc'
      ? <ChevronUp className="w-4 h-4 text-theme-accent" />
      : <ChevronDown className="w-4 h-4 text-theme-accent" />;
  };

  // Helper to get component inventory info
  const getComponentInventory = useCallback((componentId: string) => {
    const inventory = components.get(componentId);
    return {
      currentStock: inventory?.quantity || 0,
      reorderPoint: inventory?.reorderPoint || 100,
      isLowStock: inventory ? inventory.quantity < inventory.reorderPoint : false
    };
  }, [components]);

  

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-theme-text-muted" />
          <input
            type="text"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-accent focus:border-theme-accent text-theme-text-primary bg-theme-bg-secondary"
          />
        </div>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-accent focus:border-theme-accent text-theme-text-primary bg-theme-bg-secondary"
        >
          <option value="all">All Categories</option>
          {componentCategories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Components Table */}
      <div className="bg-theme-bg-content rounded-lg shadow overflow-x-auto touch-pan-x">
        <table className="min-w-[1100px] divide-y divide-theme-border">
          <thead className="bg-theme-bg-secondary">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider cursor-pointer hover:bg-theme-bg-tertiary"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Component
                  <SortIcon field="name" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                Specs
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider cursor-pointer hover:bg-theme-bg-tertiary"
                onClick={() => handleSort('manufacturer')}
              >
                <div className="flex items-center gap-1">
                  Manufacturer
                  <SortIcon field="manufacturer" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider cursor-pointer hover:bg-theme-bg-tertiary"
                onClick={() => handleSort('category')}
              >
                <div className="flex items-center gap-1">
                  Category
                  <SortIcon field="category" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider cursor-pointer hover:bg-theme-bg-tertiary"
                onClick={() => handleSort('releaseDate')}
              >
                <div className="flex items-center gap-1">
                  Release Date
                  <SortIcon field="releaseDate" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider cursor-pointer hover:bg-theme-bg-tertiary"
                onClick={() => handleSort('cost')}
              >
                <div className="flex items-center gap-1">
                  Cost
                  <SortIcon field="cost" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider cursor-pointer hover:bg-theme-bg-tertiary"
                onClick={() => handleSort('techScore')}
              >
                <div className="flex items-center gap-1">
                  Tech Score
                  <SortIcon field="techScore" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider cursor-pointer hover:bg-theme-bg-tertiary"
                onClick={() => handleSort('inventory')}
              >
                <div className="flex items-center gap-1">
                  Inventory
                  <SortIcon field="inventory" />
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider cursor-pointer hover:bg-theme-bg-tertiary"
                onClick={() => handleSort('pendingOrders')}
              >
                <div className="flex items-center gap-1">
                  Pending Orders
                  <SortIcon field="pendingOrders" />
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-theme-bg-content divide-y divide-theme-border">
            {sortedComponents.map((component, index) => {
              const inventory = getComponentInventory(component.value);
              const pendingOrders = getComponentPendingOrders(component.value);
              const autoOrderSetting = autoOrderSettings.get(component.value);
              const hasAutoOrder = autoOrderSetting && (
                autoOrderSetting.autoOrderThreshold !== null ||
                autoOrderSetting.recurringOrder?.enabled
              );

              return (
                <tr key={`${component.categoryId}-${component.value}-${index}`} className="hover:bg-theme-bg-secondary">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-theme-text-primary">{component.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-theme-text-muted">{component.specs || '-'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-theme-text-primary">{component.manufacturer}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-theme-accent/10 text-theme-accent">
                      {component.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-theme-text-primary">
                      {formatDate(component.releaseDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-theme-text-primary">{formatCurrency(component.cost)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-theme-text-primary">{component.techScore}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-theme-text-primary">
                      {inventory.currentStock}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-theme-text-primary">
                        {pendingOrders || 0}
                      </span>
                      {hasAutoOrder && (
                        <span className="relative group">
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-500/20 text-green-400 rounded cursor-default">
                            Auto
                          </span>
                          <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-10">
                            {getAutoOrderTooltipText(autoOrderSetting)}
                          </span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleOpenOrderModal(component)}
                      className="px-3 py-1.5 text-xs font-medium text-theme-accent bg-theme-accent/10 border border-theme-accent rounded-lg hover:bg-theme-accent/20 transition-colors"
                    >
                      Order
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {sortedComponents.length === 0 && (
          <div className="text-center py-8 text-theme-text-muted">
            No components found matching your criteria
          </div>
        )}
      </div>

      {/* Order Modal */}
      <ComponentOrderModal
        isOpen={isOrderModalOpen}
        onClose={handleCloseOrderModal}
        onOrder={handleModalOrder}
        component={selectedComponent}
        cash={cash}
        existingAutoOrderSetting={
          selectedComponent ? autoOrderSettings.get(selectedComponent.id) : undefined
        }
      />
    </div>
  );
}